import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, Lock, User, Building2, BriefcaseBusiness, Globe2, ArrowRight, Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { trackRegistration } from "@/utils/metaPixel";
import AuthHero from "@/components/AuthHero";
import axios from "axios";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightweight strength heuristic for the signup meter (visual guidance only —
// the backend enforces the hard minimum).
function passwordStrength(pw) {
  if (!pw) return { level: 0, label: "" };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (s <= 1) return { level: 1, label: "Weak", color: "#ef4444" };
  if (s <= 3) return { level: 2, label: "Fair", color: "#eab308" };
  return { level: 3, label: "Strong", color: "#16a34a" };
}

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const requestedRole = searchParams.get("role");
  const [isLogin, setIsLogin] = useState(mode !== "signup");
  const [accountType, setAccountType] = useState(requestedRole === "recruiter" ? "recruiter" : "candidate");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [fieldError, setFieldError] = useState({ field: "", message: "", suggestSwitch: false });
  const [loading, setLoading] = useState(false);
  const { login, register, user, getHeaders } = useAuth();
  const navigate = useNavigate();
  const returnUrl = searchParams.get("return") || searchParams.get("redirect") || "/intake";
  const strength = passwordStrength(password);

  useEffect(() => {
    // Already logged-in user visiting /auth — redirect immediately
    if (user) {
      if (returnUrl !== "/intake") { navigate(returnUrl); return; }
      if (accountType === "recruiter") { navigate("/recruiter"); return; }
      // Admins go straight to the admin panel
      if (user.is_admin) { navigate("/admin"); return; }
      // Returning users who already ran an analysis land on their dashboard —
      // they can start a new analysis themselves if they want one.
      if (user.has_analysis) { navigate("/dashboard"); return; }
      // Check entitlements to decide dashboard vs intake
      axios.get(`${API}/user/subscription`, { headers: getHeaders() })
        .then(res => {
          const d = res.data;
          const hasPaid = d?.has_season_pass || d?.plan || d?.has_paid_analysis || d?.has_free_analysis;
          navigate(hasPaid ? "/dashboard" : "/intake");
        })
        .catch(() => navigate("/intake"));
    }
  }, [user]); // eslint-disable-line

  if (user) { return null; }

  const switchMode = (toLogin) => {
    setIsLogin(toLogin);
    setFieldError({ field: "", message: "", suggestSwitch: false });
    setPassword("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFieldError({ field: "", message: "", suggestSwitch: false });
    try {
      let data;
      if (isLogin) {
        data = await login(email, password, accountType);
      } else {
        // Name is mandatory on signup — validate before anything else.
        const trimmedName = name.trim();
        if (trimmedName.length < 2) {
          setFieldError({ field: "name", message: "Please enter your name.", suggestSwitch: false });
          setLoading(false);
          return;
        }
        if (accountType === "recruiter" && companyName.trim().length < 2) {
          setFieldError({ field: "company", message: "Please enter your company name.", suggestSwitch: false });
          setLoading(false);
          return;
        }
        // Mirror the backend password policy (POS-037) for instant feedback.
        // 12–64 chars (64 caps bcrypt's 72-byte truncation) + complexity.
        if (password.length > 64) {
          setFieldError({ field: "password", message: "Password must be 64 characters or fewer.", suggestSwitch: false });
          setLoading(false);
          return;
        }
        const missing = [];
        if (password.length < 12) missing.push("at least 12 characters");
        if (!/[a-z]/.test(password)) missing.push("a lowercase letter");
        if (!/[A-Z]/.test(password)) missing.push("an uppercase letter");
        if (!/\d/.test(password)) missing.push("a number");
        if (!/[^A-Za-z0-9]/.test(password)) missing.push("a special character");
        if (missing.length) {
          setFieldError({ field: "password", message: "Password must contain " + missing.join(", ") + ".", suggestSwitch: false });
          setLoading(false);
          return;
        }
        data = await register(email, password, trimmedName, accountType === "recruiter" ? {
          account_type: "recruiter",
          company_name: companyName.trim(),
          company_website: companyWebsite.trim() || undefined,
          job_title: jobTitle.trim() || undefined,
        } : {});
        trackRegistration(email);
      }
      toast.success(isLogin ? "Welcome back!" : "Account created!");

      // Explicit ?return= always wins
      if (returnUrl !== "/intake") { navigate(returnUrl); return; }

      if (accountType === "recruiter") { navigate("/recruiter"); return; }

      // Admins go straight to the admin panel
      if (data?.user?.is_admin) { navigate("/admin"); return; }

      // Check entitlements — try login response first, fall back to subscription endpoint
      const u = data?.user;
      let hasPaid = u?.has_paid_analysis || u?.has_season_pass || u?.has_free_analysis;
      if (!hasPaid && data?.token) {
        try {
          const subRes = await axios.get(`${API}/user/subscription`, {
            headers: { Authorization: `Bearer ${data.token}` }
          });
          const d = subRes.data;
          hasPaid = d?.has_season_pass || d?.plan || d?.has_paid_analysis;
        } catch { /* fall through */ }
      }
      // Anyone who already ran an analysis lands on the dashboard, not intake.
      navigate(hasPaid || u?.has_analysis ? "/dashboard" : "/intake");
    } catch (err) {
      const detail = err.response?.data?.detail || "Something went wrong — please try again";
      // Map server errors to the field they belong to, with a one-click fix.
      if (/no account found/i.test(detail)) {
        setFieldError({ field: "email", message: detail, suggestSwitch: true });
      } else if (/incorrect password/i.test(detail)) {
        setFieldError({ field: "password", message: detail, suggestSwitch: false });
      } else if (/already registered/i.test(detail)) {
        setFieldError({ field: "email", message: detail, suggestSwitch: true });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputBase = "pl-11 h-12 rounded-xl bg-white border border-[var(--ap-line)] text-[var(--ap-ink)] placeholder:text-gray-400 focus:border-[var(--ap-violet)] focus:ring-2 focus:ring-[var(--ap-violet-soft)] focus-visible:ring-[var(--ap-violet-soft)] transition-colors";
  const inputError = "border-red-300 bg-red-50/40 focus:border-red-400 focus:ring-red-200";
  const linkCls = "font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0";

  return (
    <div className="ap-root min-h-screen flex" data-testid="auth-page">
      {/* ── Brand hero (desktop) ──────────────────────────────────────────── */}
      <AuthHero />

      {/* ── Form panel ────────────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-5 py-10" style={{ background: "var(--ap-canvas)" }}>
        <div className="w-full max-w-[420px] ap-rise" data-testid="auth-card">
          {/* mobile brand (hero is hidden < lg) — same logo asset as navbar/footer */}
          <button onClick={() => navigate("/")} className="lg:hidden bg-transparent border-0 cursor-pointer p-0 mb-7 block" aria-label="PlacementOS home">
            <img src="/logo.png" alt="PlacementOS" className="h-9 w-auto" />
          </button>

          {/* segmented mode toggle */}
          <div className="flex p-1 rounded-full bg-[var(--ap-canvas-2)] border border-[var(--ap-line)] mb-7" role="tablist">
            <button
              type="button" role="tab" aria-selected={isLogin}
              onClick={() => switchMode(true)}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all border-0 cursor-pointer ${isLogin ? "bg-white text-[var(--ap-ink)] shadow-[var(--ap-shadow-sm)]" : "bg-transparent text-[var(--ap-muted)] hover:text-[var(--ap-ink)]"}`}
              data-testid="tab-login"
            >
              Sign in
            </button>
            <button
              type="button" role="tab" aria-selected={!isLogin}
              onClick={() => switchMode(false)}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all border-0 cursor-pointer ${!isLogin ? "bg-white text-[var(--ap-ink)] shadow-[var(--ap-shadow-sm)]" : "bg-transparent text-[var(--ap-muted)] hover:text-[var(--ap-ink)]"}`}
              data-testid="tab-signup"
            >
              Sign up
            </button>
          </div>

          <div className="mb-6">
            <h2 className="ap-display text-[28px] leading-tight font-extrabold text-[var(--ap-ink)]">
              {accountType === "recruiter"
                ? (isLogin ? "Recruiter sign in" : "Create recruiter account")
                : (isLogin ? "Welcome back" : "Create your account")}
            </h2>
            <p className="text-[var(--ap-muted)] text-sm mt-1.5">
              {accountType === "recruiter"
                ? (isLogin ? "Manage your company profile and job posts." : "Start posting roles from your verified company profile.")
                : (isLogin ? "Sign in to your dashboard, reports, and resume vault." : "Get your honest ATS score in minutes — free.")}
            </p>
          </div>

          <div className="mb-6" data-testid="account-type-selector">
            {accountType === "candidate" ? (
              <button
                type="button"
                onClick={() => setAccountType("recruiter")}
                className="group inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-sm font-semibold text-[var(--ap-violet)] hover:underline cursor-pointer"
                data-testid="account-type-recruiter"
              >
                Are you a recruiter? <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </button>
            ) : (
              <div className="flex items-center justify-between gap-4 rounded-xl border border-[var(--ap-line)] bg-[var(--ap-violet-soft)] px-4 py-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <BriefcaseBusiness className="w-4 h-4 shrink-0 text-[var(--ap-violet)]" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[var(--ap-ink)]">Recruiter account</p>
                    <p className="text-xs text-[var(--ap-muted)] truncate">Your candidate access stays available.</p>
                  </div>
                </div>
              <button
                type="button"
                  onClick={() => setAccountType("candidate")}
                  className="shrink-0 bg-transparent border-0 p-0 text-xs font-bold text-[var(--ap-violet)] hover:underline cursor-pointer"
                  data-testid="account-type-candidate"
              >
                  Job seeker
              </button>
            </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            {!isLogin && (
              <div>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ap-muted)] z-10" />
                  <Input
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => { setName(e.target.value); if (fieldError.field === "name") setFieldError({ field: "", message: "", suggestSwitch: false }); }}
                    required={!isLogin}
                    autoComplete="name"
                    className={`${inputBase} ${fieldError.field === "name" ? inputError : ""}`}
                    data-testid="auth-name-input"
                  />
                </div>
                {fieldError.field === "name" && (
                  <div className="flex items-start gap-1.5 mt-1.5 ml-1" data-testid="name-error">
                    <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-red-500 text-xs">{fieldError.message}</p>
                  </div>
                )}
              </div>
            )}

            {!isLogin && accountType === "recruiter" && (
              <div className="contents" data-testid="recruiter-signup-fields">
                <div>
                  <div className="relative">
                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ap-muted)] z-10" />
                    <Input
                      placeholder="Company name"
                      value={companyName}
                      onChange={(e) => { setCompanyName(e.target.value); if (fieldError.field === "company") setFieldError({ field: "", message: "", suggestSwitch: false }); }}
                      required
                      autoComplete="organization"
                      className={`${inputBase} ${fieldError.field === "company" ? inputError : ""}`}
                      data-testid="recruiter-company-input"
                    />
                  </div>
                  {fieldError.field === "company" && <p className="text-red-500 text-xs mt-1.5 ml-1">{fieldError.message}</p>}
                </div>
                <div className="relative">
                  <Globe2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ap-muted)] z-10" />
                  <Input
                    type="url"
                    placeholder="Company website"
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    autoComplete="url"
                    className={inputBase}
                    data-testid="recruiter-website-input"
                  />
                </div>
                <div className="relative">
                  <BriefcaseBusiness className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ap-muted)] z-10" />
                  <Input
                    placeholder="Your title (for example, Talent Partner)"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    autoComplete="organization-title"
                    className={inputBase}
                    data-testid="recruiter-job-title-input"
                  />
                </div>
              </div>
            )}

            <div>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ap-muted)] z-10" />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (fieldError.field === "email") setFieldError({ field: "", message: "", suggestSwitch: false }); }}
                  required
                  autoComplete="email"
                  className={`${inputBase} ${fieldError.field === "email" ? inputError : ""}`}
                  data-testid="auth-email-input"
                />
              </div>
              {fieldError.field === "email" && (
                <div className="flex items-start gap-1.5 mt-1.5 ml-1" data-testid="email-error">
                  <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-red-500 text-xs">
                    {fieldError.message}{" "}
                    {fieldError.suggestSwitch && (
                      <button type="button" onClick={() => switchMode(!isLogin)} className={`text-[var(--ap-violet)] ${linkCls}`} data-testid="error-switch-mode">
                        {isLogin ? "Create one with this email →" : "Sign in instead →"}
                      </button>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ap-muted)] z-10" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (fieldError.field === "password") setFieldError({ field: "", message: "", suggestSwitch: false }); }}
                  onKeyUp={(e) => setCapsOn(e.getModifierState && e.getModifierState("CapsLock"))}
                  required
                  minLength={6}
                  maxLength={64}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  className={`${inputBase} pr-11 ${fieldError.field === "password" ? inputError : ""}`}
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ap-muted)] hover:text-[var(--ap-violet)] bg-transparent border-0 cursor-pointer p-0 z-10"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {capsOn && (
                <p className="text-yellow-600 text-xs mt-1.5 ml-1 flex items-center gap-1.5" data-testid="capslock-hint">
                  <AlertCircle className="w-3.5 h-3.5" /> Caps Lock is on
                </p>
              )}
              {fieldError.field === "password" && (
                <div className="flex items-start gap-1.5 mt-1.5 ml-1" data-testid="password-error">
                  <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-red-500 text-xs">
                    {fieldError.message}{" "}
                    <button type="button" onClick={() => navigate("/forgot-password")} className={`text-[var(--ap-violet)] ${linkCls}`}>
                      Reset password →
                    </button>
                  </p>
                </div>
              )}
              {/* Signup strength meter */}
              {!isLogin && password && (
                <div className="mt-2 ml-1" data-testid="password-strength">
                  <div className="flex gap-1.5 items-center">
                    {[1, 2, 3].map(i => (
                      <span key={i} className="h-1 flex-1 rounded-full transition-colors"
                        style={{ background: strength.level >= i ? strength.color : "var(--ap-line)" }} />
                    ))}
                    <span className="text-[11px] font-semibold ml-1" style={{ color: strength.color }}>{strength.label}</span>
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="ap-btn ap-btn-fire ap-btn-lg w-full mt-1 disabled:opacity-60"
              data-testid="auth-submit-btn"
            >
              {loading
                ? (<><Loader2 className="w-4 h-4 animate-spin" /> {isLogin ? "Signing in…" : "Creating account…"}</>)
                : (<>{isLogin ? "Sign In" : "Create Account"} <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          {isLogin && (
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate("/forgot-password")}
                className={`text-sm text-[var(--ap-violet)] ${linkCls}`}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
          )}

          {!isLogin && (
            <p className="text-[var(--ap-muted)] text-xs text-center mt-4 leading-relaxed">
              By creating an account you agree to our{" "}
              <button onClick={() => navigate("/terms")} className={`text-[var(--ap-violet)] ${linkCls}`}>Terms</button> and{" "}
              <button onClick={() => navigate("/privacy")} className={`text-[var(--ap-violet)] ${linkCls}`}>Privacy Policy</button>.
            </p>
          )}

          <div className="mt-6 pt-5 border-t border-[var(--ap-line)] text-center">
            <button
              onClick={() => switchMode(!isLogin)}
              className="text-[var(--ap-muted)] text-sm hover:text-[var(--ap-ink)] transition-colors bg-transparent border-0 cursor-pointer"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? (<>Don't have an account? <span className="text-[var(--ap-violet)] font-semibold">Sign up free</span></>) : (<>Already have an account? <span className="text-[var(--ap-violet)] font-semibold">Sign in</span></>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
